import moment from "moment";

export const parsePercent = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const num = Number(String(value).replace("%", "").trim());
  return Number.isFinite(num) ? num : null;
};

export const getAccuracyTone = (value) => {
  const num = parsePercent(value);
  if (num == null) return "";
  if (num < 30) return "is-low";
  if (num < 70) return "is-mid";
  return "is-high";
};

export const getScoreTone = (value) => {
  const num = parsePercent(value);
  if (num == null) return "is-mid";
  // Recent Tests ring: <34 red, 34–67 yellowish, >67 green
  if (num < 34) return "is-low";
  if (num <= 67) return "is-mid";
  return "is-high";
};

const pickNum = (row, keys) => {
  for (const key of keys) {
    if (row?.[key] === undefined || row?.[key] === null || row?.[key] === "") {
      continue;
    }
    const num = Number(row[key]);
    if (Number.isFinite(num)) return num;
  }
  return null;
};

export const extractOverallPerformanceRows = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  const candidateKeys = [
    "OverallPerformanceReport",
    "overallPerformanceReport",
    "performance_list",
    "tests",
    "rows",
    "report",
    "test_list",
    "overall_performance",
    "list",
    "recent_tests",
    "recentTests",
  ];
  for (const key of candidateKeys) {
    const value = payload[key];
    if (Array.isArray(value) && value.length > 0) return value;
  }
  for (const key of candidateKeys) {
    const value = payload[key];
    if (Array.isArray(value)) return value;
  }
  return [];
};

export const formatRecentTestDate = (raw) => {
  if (!raw) return "";
  const date = moment(
    raw,
    [
      moment.ISO_8601,
      "YYYY-MM-DD",
      "DD-MM-YYYY",
      "DD/MM/YYYY",
      "DD-MM-YY",
      "DD/MM/YY",
    ],
    true
  );
  if (!date.isValid()) return String(raw);
  if (date.isSame(moment(), "day")) return "Today";
  if (date.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  return date.format("DD-MM-YYYY");
};

export const normalizeRecentTests = (payload, limit = 2) => {
  return extractOverallPerformanceRows(payload)
    .map((raw, index) => {
      const title =
        raw?.test_name ??
        raw?.paper_name ??
        raw?.test_format ??
        raw?.name ??
        raw?.title ??
        `Test ${index + 1}`;

      const subjectMarks = [
        pickNum(raw, ["Physics", "physics", "p", "physics_marks"]),
        pickNum(raw, ["Chemistry", "chemistry", "c", "chemistry_marks"]),
        pickNum(raw, ["Maths", "maths", "m", "math_marks", "Mathematics", "mathematics"]),
      ].filter((value) => value != null);
      const obtained =
        pickNum(raw, ["obtained_marks", "score", "marks_obtained", "total_score"]) ??
        (subjectMarks.length ? subjectMarks.reduce((sum, value) => sum + value, 0) : null);

      const total =
        pickNum(raw, ["tm", "total_marks", "TM", "total_mark", "marks", "max_marks"]) ?? null;

      const percent =
        parsePercent(raw?.percent ?? raw?.percentage ?? raw?.accuracy ?? raw?.score_percent) ??
        (total && obtained != null ? Math.round((obtained / total) * 100) : null);

      const attemptedAt =
        raw?.attempt_date ??
        raw?.test_date ??
        raw?.date ??
        raw?.created_at ??
        raw?.submitted_at;

      return {
        id: raw?.paper_id ?? raw?.test_id ?? raw?.paperId ?? `${title}-${index}`,
        title,
        scoreLabel:
          obtained != null && total != null ? `${obtained}/${total}` : obtained != null ? String(obtained) : "",
        percent,
        dateLabel: formatRecentTestDate(attemptedAt),
        sortDate: attemptedAt ? moment(attemptedAt).valueOf() : 0,
      };
    })
    .filter((item) => item.title)
    .sort((a, b) => b.sortDate - a.sortDate)
    .slice(0, limit)
    .map(({ sortDate, ...item }) => item);
};

/** Maps `testWiseAnalysis` → UI cards (top `limit` from `recent_tests`). */
export const resolveRecentTestPaperType = (paperType) => {
  if (paperType === null || paperType === undefined || paperType === "") {
    return null;
  }

  const asNumber = Number(paperType);
  if (Number.isFinite(asNumber) && String(paperType).trim() !== "") {
    return asNumber;
  }

  const key = String(paperType).trim().toUpperCase();
  // Fallback only when API still sends labels instead of numeric ids.
  if (key === "PYQ") return 1;
  if (key === "OTS" || key === "AITS") return 3;
  if (key === "BITSAT") return 10;
  return null;
};

export const normalizeTestWiseRecentTests = (payload, limit = 2) => {
  const rows = Array.isArray(payload?.recent_tests) ? payload.recent_tests : [];

  return rows.slice(0, limit).map((raw, index) => {
    const paperId = raw?.paper_id;
    const title = raw?.test_name || raw?.paper_name || `Test ${index + 1}`;
    const paperType = resolveRecentTestPaperType(
      raw?.paper_type ?? raw?.paperType
    );
    const plannerTestIdRaw = Number(
      raw?.planner_test_id ?? raw?.plannerTestId ?? 0
    );
    const plannerTestId =
      Number.isFinite(plannerTestIdRaw) && plannerTestIdRaw > 0
        ? plannerTestIdRaw
        : null;
    // Prefer accuracy on the recent_tests row (or enriched from getAnalyticsReport).
    // Do NOT use score_trend.graph — that chart is historical and often stale vs latest attempt.
    const percent = parsePercent(
      raw?.accuracy ??
        raw?.accuracy_percent ??
        raw?.accuracyPercent ??
        raw?.accuracy_percentage ??
        raw?.percentage ??
        raw?.percent
    );
    // AITS-style analytics payloads (paper_type 3) need planner_test_id.
    const isAitsStyle = Number(paperType) === 3;

    return {
      id: paperId ?? `${title}-${index}`,
      paperId,
      paperType,
      plannerTestId,
      isAitsStyle,
      title,
      scoreLabel: "",
      percent,
      dateLabel: formatRecentTestDate(raw?.attempted_on),
    };
  });
};

/**
 * total_attempted = wrong_count / (1 - accuracy/100)
 * e.g. wrong=192, accuracy=20% → 192 / 0.8 = 240
 */
export const computeAttemptedFromWrongAndAccuracy = (wrongCount, accuracy) => {
  if (wrongCount === null || wrongCount === undefined || wrongCount === "") {
    return null;
  }
  const wrong = Number(wrongCount);
  const acc = parsePercent(accuracy);
  if (!Number.isFinite(wrong) || wrong < 0 || acc == null) return null;
  if (acc >= 100) return wrong === 0 ? null : wrong;
  if (acc <= 0) return Math.round(wrong);
  const total = wrong / (1 - acc / 100);
  return Number.isFinite(total) ? Math.round(total) : null;
};

const extractWeakTopicRows = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  const candidateKeys = [
    "top_weak_topics",
    "topWeakTopics",
    "weak_topics",
    "weakTopics",
    "focus_topics",
    "focusTopics",
    "topics",
    "list",
    "data",
  ];

  for (const key of candidateKeys) {
    const value = payload[key];
    if (Array.isArray(value) && value.length > 0) return value;
  }
  for (const key of candidateKeys) {
    const value = payload[key];
    if (Array.isArray(value)) return value;
  }
  return [];
};

export const normalizeFocusTopics = (payload) => {
  return extractWeakTopicRows(payload)
    .map((raw, index) => {
      const subject =
        raw?.subject_name ??
        raw?.subjectName ??
        raw?.subject ??
        raw?.sub_name ??
        "";
      const topic =
        raw?.topic_name ??
        raw?.topicName ??
        raw?.topic ??
        raw?.chapter_name ??
        raw?.chapterName ??
        raw?.name ??
        "";

      const title = [subject, topic].filter(Boolean).join(" . ") || topic || subject;
      if (!title) return null;

      const accuracy = parsePercent(
        raw?.accuracy_percentage ??
          raw?.accuracyPercentage ??
          raw?.accuracy_percent ??
          raw?.accuracyPercent ??
          raw?.accuracy ??
          raw?.percent
      );

      const wrongCount = pickNum(raw, [
        "wrong_count",
        "wrongCount",
        "incorrect_count",
        "incorrectCount",
        "wrong",
        "incorrect",
      ]);

      const attempted =
        computeAttemptedFromWrongAndAccuracy(wrongCount, accuracy) ??
        pickNum(raw, [
          "total_attempt",
          "total_attempted",
          "totalAttempt",
          "questions_attempted",
          "questionsAttempted",
          "attempted",
          "total_questions",
          "totalQuestions",
        ]);

      return {
        id:
          raw?.topic_id ??
          raw?.topicId ??
          raw?.id ??
          `${subject}-${topic}-${index}`,
        title,
        attempted,
        accuracy,
      };
    })
    .filter(Boolean);
};

export const normalizePerformanceSubjects = (weekly = {}) => {
  const raw =
    weekly?.performance ??
    weekly?.subject_performance ??
    weekly?.subjectPerformance ??
    weekly?.performance_overview ??
    weekly?.performanceOverview ??
    weekly?.subjects ??
    weekly?.subject_wise;

  if (Array.isArray(raw)) {
    return raw
      .map((item, index) => ({
        id: item?.id ?? item?.subject_id ?? `${item?.subject || item?.name || "subject"}-${index}`,
        name: item?.subject ?? item?.subject_name ?? item?.name ?? "Subject",
        accuracy:
          parsePercent(item?.accuracy ?? item?.accuracy_percent ?? item?.percent ?? item?.value) ?? null,
      }))
      .filter((item) => item.name);
  }

  if (raw && typeof raw === "object") {
    return Object.entries(raw).map(([name, value]) => ({
      id: name,
      name,
      accuracy: parsePercent(
        typeof value === "object"
          ? value?.accuracy ?? value?.accuracy_percent ?? value?.percent
          : value
      ),
    }));
  }

  return [];
};
